import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { createPortal } from "react-dom";
import { ChevronDown, Info, Building2, Send, Flag, Clock, CheckCircle2, Wallet } from "lucide-react";
import { UserAvatar, AvatarViewModal, showMessageNotification } from "../../App";
import { VerifiedExecutorBadge } from "../badges/VerifiedExecutorBadge";
import { B2BChatTip } from "../b2b/B2BChatTip";
import { BusinessStatusBadge } from "../b2b/B2BBadges";
import { EvidenceBadge } from "./EvidenceBadge";
import { IdentityVerifiedBadge } from "../badges/IdentityVerifiedBadge";
import { TaskReferenceMedia } from "./TaskReferenceMedia";
import { isDeadlineExpired } from "../../utils/deadline";
import { formatMessageTime, formatServerDateTime } from "../../utils/timeUtils";
import { isDirectPayment, fmtRub, needsSurcharge, isDirectPriceAgreed, isPriceChangeBlocking } from "../../utils/priceChange";
import { MIN_PAID_TASK_PRICE, MIN_PAID_TASK_PRICE_ERROR } from "../../utils/taskCancel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const TaskChatOnlyEnhanced = ({ task, user, showToast, onNewMessage, actions = null }) => {
 
 const [messages, setMessages] = useState([]);
 // TZ-9 п.11 (Ж25): черновик сообщения переживает закрытие чата и перезагрузку
 const draftKey = `rv-chat-draft:${user?.id}:${task.id}`;
 const [newMessage, setNewMessageRaw] = useState(() => { try { return localStorage.getItem(draftKey) || ""; } catch { return ""; } });
 const setNewMessage = (v) => { setNewMessageRaw(v); try { v ? localStorage.setItem(draftKey, v) : localStorage.removeItem(draftKey); } catch { /* приватный режим */ } };
 const [loading, setLoading] = useState(true);
 const [lastMessageCount, setLastMessageCount] = useState(0);
 const audioCtxRef = useRef(null);
 const [showPriceChange, setShowPriceChange] = useState(false);
 const [priceChangeData, setPriceChangeData] = useState({ newPrice: task.price, reason: "" });
 const [activePriceChange, setActivePriceChange] = useState(null);
 const [priceChangeLoaded, setPriceChangeLoaded] = useState(false);
 const [infoOpen, setInfoOpen] = useState(null);
 const chatEndRef = useRef(null); // Reference for auto-scroll
 const chatInputRef = useRef(null);
 const [otherUserProfile, setOtherUserProfile] = useState(null);
 const [avatarViewModal, setAvatarViewModal] = useState(null);
 const [journalistCardOpen, setJournalistCardOpen] = useState(false);
 const [showComplaintModal, setShowComplaintModal] = useState(false);
 const [complaintText, setComplaintText] = useState("");
 const [complaintSubmitting, setComplaintSubmitting] = useState(false);
 const [complaintSent, setComplaintSent] = useState(false);
 const isCustomer = String(task.customer_id) === String(user.id);
 const isExecutorSide = task.executor_id === user.id || task.discussing_executor_id === user.id;
 const directPayment = isDirectPayment(task);
 const chatOtherId = isCustomer
 ? (task.discussing_executor_id || task.executor_id)
 : task.customer_id;

 useEffect(() => {
 if (chatOtherId) {
 axios.get(`${API}/users/${chatOtherId}/public-profile`)
 .then(r =>setOtherUserProfile(r.data))
 .catch(() => {});
 }
 }, [chatOtherId]);

 useEffect(() => {
 fetchMessages();
 fetchActivePriceChange();
 const interval = setInterval(() => {
 fetchMessages();
 fetchActivePriceChange();
 }, 3000);
 return () =>clearInterval(interval);
 }, []);

 // Fetch active price change request
 const fetchActivePriceChange = async () => {
 try {
 const response = await axios.get(`${API}/tasks/${task.id}/active-price-change`);
 setActivePriceChange(response.data);
 setPriceChangeLoaded(true);
 } catch (error) {
 if (error.response?.status !== 404) {
 console.error(" Error fetching active price change:", error);
 } else {
 setActivePriceChange(null);
 setPriceChangeLoaded(true);
 }
 }
 };

 // Request price change (customer only)
 const requestPriceChange = async () => {
 if (!task.is_b2b && Number(priceChangeData.newPrice) < MIN_PAID_TASK_PRICE) {
 showToast(MIN_PAID_TASK_PRICE_ERROR, "error");
 return;
 }
 try {
 const response = await axios.post(`${API}/tasks/${task.id}/change-price`, {
 new_price: parseFloat(priceChangeData.newPrice),
 reason: priceChangeData.reason
 });
 
 showToast(response.data.message, "success");
 setShowPriceChange(false);
 fetchActivePriceChange();
 if (onNewMessage) onNewMessage();
 } catch (error) {
 showToast(error.response?.data?.detail || "Не удалось отправить запрос на изменение цены. Попробуйте ещё раз.", "error");
 }
 };

 // Respond to price change (executor only)
 const respondToPriceChange = async (action, reason = "") => {
 try {
 const response = await axios.post(`${API}/tasks/${task.id}/respond-price-change`, {
 action,
 reason
 });
 
 showToast(response.data.message, "success");
 fetchActivePriceChange();
 if (onNewMessage) onNewMessage();
 
 // Update task price if confirmed (текст результата приходит с сервера: доплата/возврат для эскроу, прямая оплата для B2B)
 if (action === "confirm" && response.data.new_price) {
 task.price = response.data.new_price;
 }
 } catch (error) {
 showToast(error.response?.data?.detail || "Не удалось отправить ответ на изменение цены. Попробуйте ещё раз.", "error");
 }
 };

 // Create YuKassa payment for price difference (customer only)
 const [creatingPayment, setCreatingPayment] = useState(false);
 
 const createPriceChangePayment = async () => {
 // Предотвращаем двойной вызов
 if (creatingPayment) return;
 
 try {
 setCreatingPayment(true);
 
 if (!activePriceChange || activePriceChange.status !== "confirmed") {
 showToast("Изменение цены не подтверждено", "error");
 return;
 }

 const priceDifference = activePriceChange.new_price - activePriceChange.old_price;
 if (priceDifference <= 0) {
 showToast("Доплата не требуется", "info");
 return;
 }

 showToast("Создание платежа...", "info");

 const response = await axios.post(`${API}/tasks/${task.id}/price-change/payment?price_change_id=${activePriceChange.id}`, {
 amount: priceDifference
 });

 if (response.data.payment_url) {
 // Открываем YuKassa в новом окне
 window.open(response.data.payment_url, '_blank');
 showToast(`Платёж на ${fmtRub(priceDifference)} ₽ создан. Завершите оплату в открывшемся окне.`, "success");
 } else {
 showToast("Платёж создан в тестовом режиме", "success");
 }

 // Обновляем активное изменение цены
 await fetchActivePriceChange();
 } catch (error) {
 console.error("Error creating price change payment:", error);
 const errorMsg = error.response?.data?.detail || "Не удалось создать платёж. Попробуйте ещё раз.";
 showToast(errorMsg, "error");
 } finally {
 setCreatingPayment(false);
 }
 };

 // Auto-scroll to latest message on load and when new messages arrive
 useEffect(() => {
 if (messages.length > 0) {
 setTimeout(() => {
 scrollToBottom();
 }, 100);
 }
 }, [messages.length]);

 // Play sound when new message arrives
 useEffect(() => {
 if (messages.length >lastMessageCount && lastMessageCount > 0) {
 // Check if the new message is not from current user
 const lastMessage = messages[messages.length - 1];
 if (lastMessage && lastMessage.sender_id !== user.id) {
 playNotificationSound();
 if (onNewMessage) onNewMessage();
 }
 }
 setLastMessageCount(messages.length);
 }, [messages, lastMessageCount, user.id, onNewMessage]);

 const playNotificationSound = () => {
   try {
     if (!audioCtxRef.current) {
       audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
     }
     const ctx = audioCtxRef.current;
     if (ctx.state === 'suspended') ctx.resume();

     const playTone = (freq, startTime, duration) => {
       const osc = ctx.createOscillator();
       const gain = ctx.createGain();
       osc.connect(gain);
       gain.connect(ctx.destination);
       osc.type = 'sine';
       osc.frequency.value = freq;
       gain.gain.setValueAtTime(0, startTime);
       gain.gain.linearRampToValueAtTime(0.18, startTime + 0.01);
       gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
       osc.start(startTime);
       osc.stop(startTime + duration);
     };

     const now = ctx.currentTime;
     playTone(880, now, 0.18);
     playTone(1100, now + 0.2, 0.22);
   } catch (error) {
     console.log("Sound notification not available:", error);
   }
 };
 const fetchMessages = async () => {
 try {
 const response = await axios.get(`${API}/tasks/${task.id}/messages`);
 const newMessages = response.data;
 
 // Check for new messages and show notifications (FIXED CONDITION)
 // Add stronger cooldown to prevent spam notifications
 if (newMessages.length >messages.length) {
 const now = Date.now();
 const cooldownKey = `notification_cooldown_${task.id}`;
 const lastNotification = parseInt(localStorage.getItem(cooldownKey) || '0');
 
 if (now - lastNotification > 30000) { // 30 second cooldown per task
 const latestMessages = newMessages.slice(messages.length);
 latestMessages.forEach((message, index) => {
 // Only show notification for messages from other users, and only the first new message
 if (message.sender_id !== user.id && index === 0) {
 showMessageNotification(message.sender_name, message.message, task.title);
 localStorage.setItem(cooldownKey, now.toString());
 }
 });
 }
 }
 
 setMessages(newMessages);
 } catch (error) {
 console.error("Error fetching messages:", error);
 } finally {
 setLoading(false);
 }
 };

 const scrollToBottom = () => {
 if (chatEndRef.current) {
 const chatContainer = chatEndRef.current.parentElement;
 if (chatContainer) {
 chatContainer.scrollTop = chatContainer.scrollHeight;
 }
 }
 };

 const sendMessage = async (e) => {
 e.preventDefault();
 if (!newMessage.trim()) return;

 try {
 const resp = await axios.post(`${API}/tasks/${task.id}/messages`, {
 task_id: task.id,
 message: newMessage
 });
 if (resp.data?.warning) showToast(resp.data.warning, "warning");
 setNewMessage("");
 if (chatInputRef.current) chatInputRef.current.style.height = 'auto';
 fetchMessages();
 if (onNewMessage) onNewMessage();
 // Force scroll after sending
 setTimeout(() => {
 scrollToBottom();
 }, 300);
 } catch (error) {
 showToast("Ошибка при отправке сообщения", "error");
 }
 };

 const submitComplaint = async () => {
 if (!complaintText.trim()) {
 showToast("Опишите суть жалобы", "error");
 return;
 }
 if (!chatOtherId) {
 showToast("Не удалось определить пользователя", "error");
 return;
 }
 try {
 setComplaintSubmitting(true);
 await axios.post(`${API}/complaints`, {
 target_user_id: chatOtherId,
 task_id: task.id,
 description: complaintText.trim()
 });
 showToast("Жалоба подана. Администрация рассмотрит её в течение 3 дней.", "success");
 setComplaintText("");
 setShowComplaintModal(false);
 setComplaintSent(true);
 } catch (error) {
 const msg = error.response?.data?.detail || "Ошибка при подаче жалобы";
 showToast(msg, "error");
 } finally {
 setComplaintSubmitting(false);
 }
 };

 const otherRole = isCustomer ? 'исполнитель' : 'заказчик';
 const otherName = isCustomer
 ? (task.executor_name || task.executor_display_name || 'Исполнитель')
 : (task.customer_name || task.customer_display_name || 'Заказчик');
 const otherId = isCustomer ? (task.executor_id || task.discussing_executor_id) : task.customer_id;
 const isDiscussionPhase = task.status === "discussing" || task.status === "pending";
 const infoExpanded = infoOpen === true; // Ж23: по умолчанию свёрнуто
 const showSurcharge = isCustomer && needsSurcharge(task, activePriceChange);
 const showPaid = isCustomer && !directPayment && activePriceChange && activePriceChange.status === "confirmed" &&
 activePriceChange.payment_status === "paid" && activePriceChange.new_price > activePriceChange.old_price;
 const showDirectAgreed = isDirectPriceAgreed(task, activePriceChange);
 const hasBadges = otherRole === 'исполнитель' && (otherUserProfile?.is_verified_executor || otherUserProfile?.identity_verified ||
 (task?.is_b2b && otherUserProfile?.business_status && otherUserProfile.business_status !== 'none') ||
 otherUserProfile?.public_profile_consent || otherUserProfile?.journalist_verified);

 const cardStyle = (bg, border) => ({ background: bg, border: `1px solid ${border}`, borderRadius: '0.75rem' });

 return (
 <div className="flex flex-col h-full min-h-0 overflow-hidden" data-testid="discussion-chat-root">
 {/* Модальное окно жалобы */}
 {showComplaintModal && createPortal(
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{zIndex:10050}}>
 <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
 <h3 className="text-base font-semibold text-gray-900 mb-1">Пожаловаться на пользователя</h3>
 <p className="text-sm text-gray-500 mb-4">Жалоба будет рассмотрена администрацией в течение 3 рабочих дней.
 </p>
 <textarea
 data-testid="complaint-description-input"
 value={complaintText}
 onChange={(e) =>setComplaintText(e.target.value)}
 placeholder="Опишите, что произошло. Чем подробнее — тем быстрее рассмотрим."
 rows={4}
 className="w-full p-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
 />
 <div className="flex gap-2 mt-4">
 <button
 data-testid="complaint-submit-btn"
 onClick={submitComplaint}
 disabled={complaintSubmitting || !complaintText.trim()}
 className="flex-1 bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 disabled:opacity-50 text-sm font-medium"
 >
 {complaintSubmitting ? "Отправляем..." : "Подать жалобу"}
 </button>
 <button
 onClick={() => { setShowComplaintModal(false); setComplaintText(""); }}
 className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-300 text-sm"
 >Отмена
 </button>
 </div>
 </div>
 </div>
 , document.body)}

 {/* ===== ШАПКА: собеседник (компактно) ===== */}
 <div className="flex-shrink-0 px-4 py-2.5 flex items-center gap-3 border-b" data-testid="chat-participant-header" style={{background:'var(--rv-white)', borderColor:'var(--rv-border)'}}>
 {isDeadlineExpired(task) && <span data-testid="chat-deadline-expired-badge" className="order-last text-xs font-semibold px-2 py-0.5 rounded flex-shrink-0" style={{background:'#FEF2F2', color:'#B91C1C', border:'1px solid #FECACA'}}>Срок истёк</span>}
 <UserAvatar
 user={{name: otherName}}
 userId={otherId}
 size={36}
 onClick={() =>setAvatarViewModal({ userId: otherId, name: otherName, statusText: otherUserProfile?.status_text })}
 />
 <div className="flex-1 min-w-0">
 <div className="flex items-baseline gap-2 flex-wrap">
 <p className="text-sm font-semibold truncate" style={{color:'var(--rv-navy)', fontFamily:'Manrope,sans-serif'}}>{otherName}</p>
 <p className="text-xs" style={{color:'var(--rv-text-muted)'}}>
 {otherRole.charAt(0).toUpperCase() + otherRole.slice(1)}
 {otherUserProfile && (
 <>
 {' · '}
 <span className="font-semibold" style={{color:'var(--rv-teal)'}}>
 {otherUserProfile.rating > 0 ? otherUserProfile.rating.toFixed(1) : 'нет оценок'}
 {otherUserProfile.total_reviews > 0 && ` (${otherUserProfile.total_reviews})`}
 </span>
 {otherUserProfile.role === 'executor' && otherUserProfile.total_tasks > 0 && ` · ${otherUserProfile.total_tasks} заданий`}
 </>
 )}
 </p>
 </div>
 {hasBadges && (
 <div className="flex items-center gap-1.5 flex-wrap mt-1">
 {otherUserProfile?.is_verified_executor && <VerifiedExecutorBadge size="xs" showLabel={true} />}
 {task?.is_b2b && otherUserProfile?.business_status && otherUserProfile.business_status !== 'none' && (
 <BusinessStatusBadge status={otherUserProfile.business_status} testId="chat-executor-business-status-badge" />
 )}
 {otherUserProfile?.identity_verified && <IdentityVerifiedBadge size="xs" showLabel={true} />}
 {otherUserProfile?.public_profile_consent && (
 <a
 data-testid="executor-public-profile-link"
 href={`${BACKEND_URL}/api/seo/ispolnitel/${chatOtherId}`}
 target="_blank"
 rel="noopener noreferrer"
 className="text-xs font-medium underline"
 style={{ color: 'var(--rv-teal)' }}
 >Профиль и отзывы</a>
 )}
 {otherUserProfile?.journalist_verified && (
 <button
 type="button"
 onClick={() =>setJournalistCardOpen(v => !v)}
 className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium hover:bg-blue-200 transition-colors"
 >Журналист <ChevronDown size={11} style={{transform: journalistCardOpen ? 'rotate(180deg)' : 'none', transition:'transform 0.15s'}} />
 </button>
 )}
 </div>
 )}
 {otherUserProfile?.status_text && (
 <p className="text-xs italic truncate mt-0.5" style={{color:'var(--rv-text-muted)'}}>"{otherUserProfile.status_text}"</p>
 )}
 </div>
 {chatOtherId && (
 complaintSent ? (
 <span className="text-xs flex-shrink-0" style={{color:'var(--rv-text-muted)'}}>Жалоба подана</span>
 ) : (
 <button
 data-testid="report-user-btn"
 onClick={() =>setShowComplaintModal(true)}
 title="Пожаловаться на пользователя"
 className="flex-shrink-0 inline-flex items-center gap-1 text-xs rounded-md px-2 py-1 transition-colors"
 style={{color:'var(--rv-error)', border:'1px solid rgba(220,38,38,0.25)', background:'transparent'}}
 ><Flag size={11} />Пожаловаться
 </button>
 )
 )}
 </div>
 {avatarViewModal && (
 <AvatarViewModal
 userId={avatarViewModal.userId}
 name={avatarViewModal.name}
 statusText={avatarViewModal.statusText}
 onClose={() =>setAvatarViewModal(null)}
 />
 )}

 {/* Раскрывающаяся карточка верифицированного журналиста */}
 {journalistCardOpen && otherUserProfile?.journalist_verified && isCustomer && (
 <div className="flex-shrink-0 border-b bg-blue-50 px-4 py-3" style={{borderColor:'var(--rv-border)'}}>
 <p className="text-xs font-semibold text-blue-900 mb-2">Данные верификации журналиста</p>
 <div className="space-y-2">
 {otherUserProfile.specializations?.length > 0 && (
 <div>
 <p className="text-xs text-gray-500 mb-1">Специализация:</p>
 <div className="flex flex-wrap gap-1">
 {otherUserProfile.specializations.map(s => (
 <span key={s} className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">{s}</span>
 ))}
 </div>
 </div>
 )}
 {otherUserProfile.equipment?.length > 0 && (
 <div>
 <p className="text-xs text-gray-500 mb-1">Оборудование:</p>
 <div className="flex flex-wrap gap-1">
 {otherUserProfile.equipment.map(e => (
 <span key={e} className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">{e}</span>
 ))}
 </div>
 </div>
 )}
 {otherUserProfile.work_region && (
 <p className="text-xs text-gray-500">Регион работы: <span className="text-gray-800 font-medium">{otherUserProfile.work_region}</span></p>
 )}
 {otherUserProfile.media_organization && (
 <p className="text-xs text-gray-500">Организация: <span className="text-gray-800 font-medium">{otherUserProfile.media_organization}</span></p>
 )}
 </div>
 </div>
 )}

 {/* Ж23 (TZ-7 п.8): единая прокрутка — инфоблок, фото и сообщения скроллятся вместе, блоки не перекрывают переписку */}
 <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain" data-testid="chat-messages-area" style={{background:'var(--rv-white)'}}>
 {/* ===== ЕДИНЫЙ СВОРАЧИВАЕМЫЙ ИНФОБЛОК: совет + условия B2B ===== */}
 {isExecutorSide && !isCustomer && (
 <div className="px-4 py-1.5 border-b" style={{borderColor:'var(--rv-border)', background:'var(--rv-paper)'}}>
 <EvidenceBadge task={task} testId="chat-evidence-badge" />
 </div>
 )}
 <div className="border-b" style={{borderColor:'var(--rv-border)', background:'var(--rv-paper)'}}>
 <button
 type="button"
 data-testid="chat-info-toggle"
 aria-expanded={infoExpanded}
 onClick={() => setInfoOpen(!infoExpanded)}
 className="w-full px-4 min-h-[40px] flex items-center gap-2 text-left"
 style={{background:'transparent', border:'none', cursor:'pointer'}}
 >
 <Info size={14} className="flex-shrink-0" style={{color:'var(--rv-warning)'}} />
 <span className="text-xs font-semibold flex-shrink-0" style={{color:'var(--rv-text-secondary)'}}>Подсказки и условия</span>
 <span className="flex items-center gap-1.5 flex-1 min-w-0 overflow-hidden">
 {!infoExpanded && (
 <span className="text-xs truncate" style={{color:'var(--rv-text-muted)'}}>Согласуйте объём, стоимость и критерии результата до принятия</span>
 )}
 {task?.is_b2b && (
 <span data-testid="chat-b2b-chip" className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0" style={{background:'#E0F2FE', color:'#0C4A6E'}}>
 <Building2 size={11} /><span className="hidden sm:inline">Организация · </span>оплата напрямую
 </span>
 )}
 </span>
 <ChevronDown size={15} className="flex-shrink-0" style={{color:'var(--rv-text-muted)', transform: infoExpanded ? 'rotate(180deg)' : 'none', transition:'transform 0.15s'}} />
 </button>
 {infoExpanded && (
 <div data-testid="chat-info-panel">
 <div className="px-4 py-2 text-xs flex items-start gap-2" data-testid="discussion-chat-tip" style={{background:'#FFFBEB', borderTop:'1px solid #FDE68A', color:'#92400E'}}>
 <span>Совет:</span>
 <span>согласуйте здесь объём работы, итоговую стоимость и критерии результата до того, как исполнитель примет задание в работу.</span>
 </div>
 <B2BChatTip task={task} />
 </div>
 )}
 </div>

 {/* Фото, приложенные заказчиком к заданию — видны на этапе обсуждения */}
 <TaskReferenceMedia task={task} />

 {/* ===== СООБЩЕНИЯ ===== */}
 <div className="px-3 sm:px-4 py-3 space-y-2.5" data-testid="chat-messages-list">
 {loading ? (
 <div className="text-center text-sm py-6" style={{color:'var(--rv-text-muted)'}}>Загрузка сообщений...</div>
 ) : messages.length === 0 ? (
 <div className="text-center py-8" style={{color:'var(--rv-text-muted)'}}>
 <p className="mb-1 text-sm font-medium" style={{color:'var(--rv-text-secondary)'}}>Начните обсуждение</p>
 <p className="text-xs">Обсудите детали задания перед принятием</p>
 </div>
 ) : (
 messages.map((message, index) => {
 const isSystem = message.sender_id === 'system';
 if (isSystem) {
 return (
 <div key={index} className="flex justify-center" data-testid="chat-system-message">
 <div className="text-[13px] leading-snug px-3 py-1.5 rounded-2xl text-center max-w-md" style={{background:'var(--rv-teal-light)', color:'var(--rv-teal-dark)'}}>
 {message.message}
 </div>
 </div>
 );
 }
 const isMe = String(message.sender_id) === String(user.id);
 const isCustomerSender = String(task.customer_id) === String(message.sender_id);
 const senderLabel = isMe ? 'Вы' : (isCustomerSender ? (task.customer_name || 'Заказчик') : (task.executor_name || 'Исполнитель'));
 const senderAvatarId = isMe ? user.id : (isCustomerSender ? task.customer_id : (task.executor_id || task.discussing_executor_id));

 return (
 <div
 key={index}
 className={`flex items-end gap-2 ${isMe ? 'justify-end' : 'justify-start'}`}
 >
 {!isMe && (
 <UserAvatar user={{name: senderLabel}} userId={senderAvatarId} size={28} />
 )}
 <div className="rv-chat-bubble max-w-[82%] sm:max-w-md px-3.5 py-2" data-testid="chat-message-bubble" style={isMe
 ? { background:'var(--rv-teal)', color:'white', borderRadius:'1.1rem 1.1rem 0.3rem 1.1rem' }
 : { background:'var(--rv-paper)', color:'var(--rv-text-primary)', border:'1px solid var(--rv-border)', borderRadius:'1.1rem 1.1rem 1.1rem 0.3rem' }}>
 <div className="rv-chat-sender" style={{opacity:0.8}}>{senderLabel}</div>
 <div className="rv-chat-text break-words whitespace-pre-wrap" style={{overflowWrap:'anywhere'}}>{message.message}</div>
 <div className="rv-chat-time text-right" style={{opacity:0.72}}>
 {formatMessageTime(message.created_at)}
 </div>
 </div>
 {isMe && (
 <UserAvatar user={user} size={28} />
 )}
 </div>
 );
 })
 )}
 </div>
 {/* Auto-scroll target — прямой потомок контейнера прокрутки */}
 <div ref={chatEndRef} />
 </div>

 {/* ===== НИЖНИЙ ДОК: статусы цены, поле ввода, действия — закреплён внизу, не перекрывается ===== */}
 <div className="flex-shrink-0 border-t bg-white overflow-y-auto" data-testid="chat-dock" style={{borderColor:'var(--rv-border)', maxHeight:'52%'}}>
 <div className="p-3 space-y-2.5">

 {/* Заказчик: ожидается ответ исполнителя */}
 {isCustomer && activePriceChange?.status === "proposed" && (
 <div className="px-3 py-2.5 flex items-start gap-2.5" data-testid="price-change-waiting" style={cardStyle('#FFF7ED', '#FDBA74')}>
 <Clock size={16} className="flex-shrink-0 mt-0.5" style={{color:'#C2410C'}} />
 <div className="text-sm min-w-0" style={{color:'#9A3412'}}>
 <p className="font-semibold">Ожидается ответ исполнителя</p>
 <p>Предложена новая цена: <strong>{fmtRub(activePriceChange.old_price)} ₽ → {fmtRub(activePriceChange.new_price)} ₽</strong></p>
 {activePriceChange.reason && <p>Причина: {activePriceChange.reason}</p>}
 <p className="text-xs mt-0.5" style={{color:'#C2410C'}}>Отправлено: {formatServerDateTime(activePriceChange.created_at)}</p>
 </div>
 </div>
 )}

 {/* Исполнитель: предложение изменения цены */}
 {isExecutorSide && activePriceChange?.status === "proposed" && (
 <div className="px-3 py-2.5" data-testid="price-change-offer" style={cardStyle('#EFF6FF', '#93C5FD')}>
 <p className="text-sm font-semibold" style={{color:'#1E3A8A'}}>Предложение изменения цены</p>
 <p className="text-sm" style={{color:'#1E40AF'}}>Заказчик предлагает: <strong>{fmtRub(activePriceChange.old_price)} ₽ → {fmtRub(activePriceChange.new_price)} ₽</strong></p>
 {activePriceChange.reason && <p className="text-sm mb-1" style={{color:'#1E40AF'}}>Причина: {activePriceChange.reason}</p>}
 <div className="flex gap-2 mt-2">
 <button
 data-testid="price-change-confirm-btn"
 onClick={() =>respondToPriceChange("confirm")}
 className="reversia-btn reversia-btn-green reversia-btn-sm flex-1 justify-center"
 >Согласиться
 </button>
 <button
 data-testid="price-change-reject-btn"
 onClick={() => {
 const reason = prompt("Причина отклонения (опционально):");
 respondToPriceChange("reject", reason || "");
 }}
 className="reversia-btn reversia-btn-red reversia-btn-sm flex-1 justify-center"
 >Отклонить
 </button>
 </div>
 </div>
 )}

 {/* B2B / прямая оплата: цена согласована, платёжных шагов нет (видят обе стороны) */}
 {showDirectAgreed && (
 <div className="px-3 py-2.5 flex items-start gap-2.5" data-testid="b2b-price-agreed" style={cardStyle('var(--rv-teal-light)', '#5EEAD4')}>
 <CheckCircle2 size={16} className="flex-shrink-0 mt-0.5" style={{color:'var(--rv-teal-dark)'}} />
 <p className="text-sm" style={{color:'var(--rv-teal-dark)'}}>
 <strong>Новая цена согласована: {fmtRub(activePriceChange.new_price)} ₽.</strong>{' '}
 Оплата — напрямую исполнителю по его реквизитам после выполнения.
 </p>
 </div>
 )}

 {/* Эскроу: заказчику требуется доплата */}
 {showSurcharge && (
 <div className="px-3 py-2.5" data-testid="price-change-surcharge" style={cardStyle('#ECFDF5', '#6EE7B7')}>
 <p className="text-sm font-semibold" style={{color:'#065F46'}}>Изменение цены подтверждено</p>
 <p className="text-sm" style={{color:'#047857'}}>Исполнитель согласился на новую цену: <strong>{fmtRub(activePriceChange.old_price)} ₽ → {fmtRub(activePriceChange.new_price)} ₽</strong></p>
 <p className="text-sm mb-2" style={{color:'#047857'}}>Требуется доплата: <strong className="text-lg">{fmtRub(activePriceChange.new_price - activePriceChange.old_price)} ₽</strong></p>
 <button
 data-testid="price-change-pay-btn"
 onClick={createPriceChangePayment}
 disabled={creatingPayment}
 className="reversia-btn reversia-btn-teal reversia-btn-md w-full justify-center"
 >
 <Wallet size={15} />
 {creatingPayment ? 'Создание платежа...' : `Оплатить доплату ${fmtRub(activePriceChange.new_price - activePriceChange.old_price)} ₽`}
 </button>
 <p className="text-xs mt-1.5" style={{color:'#059669'}}>После оплаты доплаты исполнитель сможет продолжить работу по новой цене</p>
 </div>
 )}

 {/* Эскроу: доплата оплачена */}
 {showPaid && (
 <div className="px-3 py-2.5" data-testid="price-change-paid" style={cardStyle('#EFF6FF', '#93C5FD')}>
 <p className="text-sm font-semibold" style={{color:'#1E3A8A'}}>Доплата успешно оплачена</p>
 <p className="text-sm" style={{color:'#1E40AF'}}>Цена обновлена: <strong>{fmtRub(activePriceChange.old_price)} ₽ → {fmtRub(activePriceChange.new_price)} ₽</strong>. Доплата <strong>{fmtRub(activePriceChange.new_price - activePriceChange.old_price)} ₽</strong> оплачена через ЮKassa.</p>
 </div>
 )}

 {/* Форма изменения цены (заказчик) */}
 {isCustomer && isDiscussionPhase && showPriceChange && (
 <div className="p-3" data-testid="price-change-form" style={cardStyle('#FFFBEB', '#FDE68A')}>
 <h4 className="text-sm font-semibold mb-2" style={{color:'#92400E'}}>Изменение цены</h4>
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
 <div>
 <label className="block text-xs font-medium mb-1" style={{color:'var(--rv-text-secondary)'}}>Новая цена (текущая: {fmtRub(task.price)} ₽)</label>
 <input
 type="number"
 data-testid="price-change-new-price-input"
 value={priceChangeData.newPrice}
 onChange={(e) =>setPriceChangeData({...priceChangeData, newPrice: e.target.value})}
 className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm bg-white"
 min={task.is_b2b ? 1 : MIN_PAID_TASK_PRICE}
 placeholder="Введите новую цену в рублях"
 />
 </div>
 <div>
 <label className="block text-xs font-medium mb-1" style={{color:'var(--rv-text-secondary)'}}>Причина изменения (опционально)</label>
 <input
 type="text"
 data-testid="price-change-reason-input"
 value={priceChangeData.reason}
 onChange={(e) =>setPriceChangeData({...priceChangeData, reason: e.target.value})}
 className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm bg-white"
 placeholder="Например: добавляем видеосъёмку"
 />
 </div>
 </div>
 <div className="flex gap-2 mt-2.5">
 <button
 data-testid="price-change-submit-btn"
 onClick={requestPriceChange}
 disabled={!priceChangeData.newPrice || Number(priceChangeData.newPrice) === Number(task.price)}
 className="reversia-btn reversia-btn-yellow reversia-btn-sm flex-1 justify-center"
 >Предложить
 </button>
 <button
 data-testid="price-change-cancel-btn"
 onClick={() =>setShowPriceChange(false)}
 className="reversia-btn reversia-btn-quiet reversia-btn-sm flex-1 justify-center"
 >Отмена
 </button>
 </div>
 </div>
 )}

 {/* Поле ввода */}
 <form onSubmit={sendMessage} className="flex gap-2 items-end">
 <textarea
 ref={chatInputRef}
 data-testid="chat-message-input"
 value={newMessage}
 onChange={(e) => {
 setNewMessage(e.target.value);
 e.target.style.height = 'auto';
 e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
 }}
 onKeyDown={(e) => {
 if (e.key === 'Enter' && !e.shiftKey) {
 e.preventDefault();
 if (newMessage.trim()) sendMessage(e);
 }
 }}
 placeholder={`Напишите сообщение ${isCustomer ? 'исполнителю' : 'заказчику'}...`}
 rows={1}
 style={{ resize: 'none', overflow: 'auto', minHeight: '44px', maxHeight: '120px', border:'1px solid var(--rv-border)', background:'var(--rv-paper)' }}
 className="rv-chat-input flex-1 px-3.5 py-2.5 rounded-2xl focus:outline-none focus:ring-2 focus:ring-teal-500"
 />
 <button
 type="submit"
 data-testid="chat-send-btn"
 disabled={!newMessage.trim()}
 className="reversia-btn reversia-btn-teal flex-shrink-0 justify-center rounded-2xl"
 style={{height:'44px', minWidth:'44px', padding:'0 0.9rem', opacity: newMessage.trim() ? 1 : 0.55}}
 aria-label="Отправить"
 >
 <Send size={16} /><span className="hidden sm:inline">Отправить</span>
 </button>
 </form>

 {/* Действия: изменить цену (заказчик) + кнопки из родителя */}
 {((isCustomer && isDiscussionPhase && priceChangeLoaded && !isPriceChangeBlocking(task, activePriceChange) && !showPriceChange) || actions) && (
 <div className="rv-chat-actions" data-testid="chat-actions-row">
 {isCustomer && isDiscussionPhase && priceChangeLoaded && !isPriceChangeBlocking(task, activePriceChange) && !showPriceChange && (
 <button
 data-testid="price-change-open-btn"
 onClick={() => { setPriceChangeData({ newPrice: task.price, reason: "" }); setShowPriceChange(true); }}
 className="reversia-btn reversia-btn-quiet reversia-btn-md justify-center"
 >{/* TZ8-7: кнопка в общем стиле дизайн-системы */}<Wallet size={14} className="inline mr-1.5" style={{color:'var(--rv-teal)'}} />Изменить цену задания
 </button>
 )}
 {actions}
 </div>
 )}
 </div>
 </div>
 </div>
 );
};
